import React, { useState, useEffect } from 'react';
import { supabase, canAccess } from './supabase';
import { Select, Card, CardHeader, EmptyState, Badge, ENTITY_LABELS } from './Shared';

const money = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const todayStr = () => new Date().toLocaleDateString('en-CA');
const DAY = 86400000;

function statusOf(i) {
  if (Number(i.balance) <= 0) return 'Paid';
  const overdue = i.due_date && i.due_date < todayStr();
  if (Number(i.balance) < Number(i.total_amt)) return overdue ? 'Overdue' : 'Partial';
  return overdue ? 'Overdue' : 'Open';
}
const VARIANT = { Paid: 'green', Partial: 'amber', Open: 'blue', Overdue: 'red' };

// Customer invoices and collections, mirrored from QuickBooks every 15 minutes.
export default function ClientInvoicesPage({ profile }) {
  const [rows, setRows] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [entity, setEntity] = useState('');
  const [status, setStatus] = useState('open');
  const [loadError, setLoadError] = useState('');
  const entities = Object.keys(ENTITY_LABELS).filter(e => canAccess(profile, e));

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('qbo_invoices').select('*').eq('is_deleted', false).order('txn_date', { ascending: false }).limit(2000);
      setLoadError(error ? error.message : '');
      setRows(data || []);
      setSyncedAt((data || []).reduce((m, r) => (r.synced_at > m ? r.synced_at : m), '') || null);
    })();
  }, []);

  const visible = rows.filter(r => !entity || r.entity === entity).map(r => ({ ...r, st: statusOf(r) }));
  const shown = visible.filter(r => status === 'all' || (status === 'open' ? r.st !== 'Paid' : r.st === status));
  const openTotal = visible.filter(r => r.st !== 'Paid').reduce((s, r) => s + Number(r.balance), 0);
  const overdueTotal = visible.filter(r => r.st === 'Overdue').reduce((s, r) => s + Number(r.balance), 0);
  const aging = [0, 0, 0, 0];
  visible.filter(r => r.st === 'Overdue').forEach(r => {
    const days = Math.floor((Date.now() - new Date(r.due_date + 'T00:00:00').getTime()) / DAY);
    aging[days <= 30 ? 0 : days <= 60 ? 1 : days <= 90 ? 2 : 3] += Number(r.balance);
  });
  const byEntity = {};
  visible.forEach(r => { const b = (byEntity[r.entity] = byEntity[r.entity] || { open: 0, overdue: 0 }); if (r.st !== 'Paid') b.open += Number(r.balance); if (r.st === 'Overdue') b.overdue += Number(r.balance); });

  return (
    <div>
      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: '1.6' }}>
        Customer invoices pulled from QuickBooks. Nothing to type here — create and collect invoices in QuickBooks and they appear within 15 minutes.
        {syncedAt ? ' Last synced ' + new Date(syncedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + '.' : ''}
      </div>
      {loadError && <Card><div style={{ color: '#B91C1C', fontSize: '13px' }}>Could not load invoices: {loadError}. Run migrations/003_qbo_cash_and_royalties.sql, then connect QuickBooks.</div></Card>}

      <div className="metrics-grid">
        <div className="metric-card"><div className="metric-label">Outstanding</div><div className="metric-value blue">{money(openTotal)}</div><div className="metric-sub">{visible.filter(r => r.st !== 'Paid').length} open invoices</div></div>
        <div className="metric-card"><div className="metric-label">Overdue</div><div className={'metric-value ' + (overdueTotal > 0 ? 'red' : 'green')}>{money(overdueTotal)}</div><div className="metric-sub">past due date</div></div>
        <div className="metric-card"><div className="metric-label">1–30 days late</div><div className="metric-value amber">{money(aging[0])}</div></div>
        <div className="metric-card"><div className="metric-label">60+ days late</div><div className={'metric-value ' + (aging[2] + aging[3] > 0 ? 'red' : 'green')}>{money(aging[2] + aging[3])}</div><div className="metric-sub">31–60: {money(aging[1])}</div></div>
      </div>

      <Card>
        <CardHeader title="By entity" />
        <table className="data-table">
          <thead><tr><th>Entity</th><th style={{ textAlign: 'right' }}>Outstanding</th><th style={{ textAlign: 'right' }}>Overdue</th></tr></thead>
          <tbody>{entities.map(e => (<tr key={e}><td>{ENTITY_LABELS[e]}</td><td style={{ textAlign: 'right' }}>{money((byEntity[e] || {}).open)}</td><td style={{ textAlign: 'right', color: (byEntity[e] || {}).overdue > 0 ? '#B91C1C' : undefined }}>{money((byEntity[e] || {}).overdue)}</td></tr>))}</tbody>
        </table>
      </Card>

      <Card>
        <CardHeader title="Invoices" hint={shown.length + ' shown'} />
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <div style={{ minWidth: '200px' }}><Select value={entity} onChange={e => setEntity(e.target.value)} options={entities.map(e => ({ value: e, label: ENTITY_LABELS[e] }))} placeholder="All entities" /></div>
          <div style={{ minWidth: '160px' }}><Select value={status} onChange={e => setStatus(e.target.value)} options={[{ value: 'open', label: 'Unpaid' }, { value: 'Overdue', label: 'Overdue' }, { value: 'Paid', label: 'Paid' }, { value: 'all', label: 'All' }]} /></div>
        </div>
        {!shown.length ? <EmptyState text="No invoices match." /> : (
          <table className="data-table">
            <thead><tr><th>Invoice</th><th>Entity</th><th>Customer</th><th>Dated</th><th>Due</th><th style={{ textAlign: 'right' }}>Total</th><th style={{ textAlign: 'right' }}>Balance</th><th>Status</th></tr></thead>
            <tbody>{shown.slice(0, 500).map(r => (
              <tr key={r.id}><td>{r.doc_number || r.qbo_id}</td><td>{ENTITY_LABELS[r.entity]}</td><td>{r.customer_name || '—'}</td><td>{fmtDate(r.txn_date)}</td><td>{fmtDate(r.due_date)}</td>
                <td style={{ textAlign: 'right' }}>{money(r.total_amt)}</td><td style={{ textAlign: 'right' }}>{money(r.balance)}</td><td><Badge variant={VARIANT[r.st]}>{r.st}</Badge></td></tr>
            ))}</tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
